import { useState, useContext } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { MainContext } from "../../../../main/context/MainContextProvider";
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export enum LossType {
    EVITAVEL = "Gerenciável",
    PREVISTA = "Prevista"
}

export type UsinaDetail = {
    name: string;
    eventos?: number;
    value: number;
};

export type LossCategoryCardProps = {
    title: string;
    type: LossType;
    eventos?: number;
    percentage: number;
    usinas: number;
    totalValue: string;
    valueSuffix?: string;
    usinaDetails: UsinaDetail[];
};

export default function LossCategoryCard({
    title,
    type,
    eventos,
    percentage,
    usinas,
    totalValue,
    valueSuffix,
    usinaDetails
}: LossCategoryCardProps) {
    const { theme } = useContext(MainContext);
    const [expanded, setExpanded] = useState(true);

    const isEvitavel = type === LossType.EVITAVEL;
    const statusColor = isEvitavel ? "#f59e0b" : "#60A5FA";
    const statusBgColor = isEvitavel ? "rgba(251, 191, 36, 0.1)" : "rgba(36, 59, 96, 0.3)";
    const statusBorderColor = isEvitavel ? "rgba(251, 191, 36, 0.3)" : "rgba(36, 59, 96, 1)";
    const progressBarColor = isEvitavel ? "#f59e0b" : "#60A5FA";

    // Formatar duração: pegar apenas a parte antes do ponto
    const formattedDuration = totalValue.split('.')[0];
    const formattedValue = totalValue;

    return (
        <Box
            sx={{
                backgroundColor: "#202020b0",
                border: `1px solid #334155`,
                borderRadius: "8px",
                padding: "8px 14px",
                marginBottom: "12px"
            }}
        >
            {/* Header Section */}
            <Box sx={{ marginBottom: "8px" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {isEvitavel ? (
                            <ErrorOutlineIcon sx={{ color: statusColor, fontSize: 20 }} />
                        ) : (
                            <TrendingDownIcon sx={{ color: statusColor, fontSize: 20 }} />
                        )}
                        <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                            {title}
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: statusBgColor,
                                border: `1px solid ${statusBorderColor}`,
                                borderRadius: "12px",
                                paddingX: "8px",
                                paddingY: "2px"
                            }}
                        >
                            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: statusColor }}>
                                {type}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-end", gap: "16px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", gap: "8px" }}>
                            <Typography sx={{ fontSize: "14px", fontWeight: "700" }}>
                                {valueSuffix ? formattedValue : formattedDuration}
                            </Typography>
                            <Typography sx={{ fontSize: "11px", color: theme.secondaryTextColor }}>
                                {valueSuffix || 'h'}
                            </Typography>
                        </Box>
                        <Box
                            onClick={() => setExpanded(!expanded)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                                paddingY: "8px",
                                // borderTop: `1px solid ${theme?.cardBorderColor || "#374151"}`,
                                "&:hover": {
                                    opacity: 0.8
                                }
                            }}
                        >
                            <KeyboardArrowDownIcon
                                sx={{
                                    fontSize: 20,
                                    color: theme.secondaryTextColor,
                                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.3s"
                                }}
                            />
                            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: theme.secondaryTextColor }}>
                                Detalhamento por Usina
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Stats Row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "12px" }}>
                    {eventos !== undefined && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor }}>
                                {eventos} {eventos === 1 ? 'evento' : 'eventos'}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ width: "4px", height: "4px", borderRadius: "50%", backgroundColor: theme.secondaryTextColor }} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor }}>
                            {percentage.toFixed(1)}% do total
                        </Typography>
                    </Box>
                    <Box sx={{ width: "4px", height: "4px", borderRadius: "50%", backgroundColor: theme.secondaryTextColor }} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor }}>
                            {usinas} {usinas === 1 ? 'usina' : 'usinas'}
                        </Typography>
                    </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ width: "100%", height: "6px", backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: "3px", overflow: "hidden", marginBottom: "8px" }}>
                    <Box
                        sx={{
                            width: `${percentage}%`,
                            height: "100%",
                            backgroundColor: progressBarColor,
                            transition: "width 0.3s ease"
                        }}
                    />
                </Box>
            </Box>

            <Collapse in={expanded}>
                <Box sx={{ paddingTop: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {usinaDetails.map((usina, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                paddingX: "12px",
                                paddingY: "10px",
                                backgroundColor: "rgba(255, 255, 255, 0.02)",
                                borderRadius: "6px",
                                transition: "all 0.2s",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.05)"
                                }
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                                <Box
                                    sx={{
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor: statusColor,
                                    }}
                                />
                                <Box>
                                    <Typography sx={{ fontSize: "13px", fontWeight: "500", textAlign: "left" }}>
                                        {usina.name}
                                    </Typography>
                                    {!!usina.eventos && (
                                        <Typography sx={{ fontSize: "11px", color: theme.secondaryTextColor, textAlign: "left" }}>
                                            {usina.eventos} {usina.eventos === 1 ? 'evento' : 'eventos'}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                            <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                                {valueSuffix ? usina.value.toFixed(2) : usina.value.toFixed(1)}
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, marginLeft: "4px" }}>
                                {valueSuffix || 'h'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}
